import React, { useEffect, useState, useMemo } from "react";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { useAuth } from "../../AuthContext";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { InputSwitch } from "primereact/inputswitch";

import "primeicons/primeicons.css";

// Importando os diálogos reutilizáveis
import DialogSucesso from "../Agendas/DialogSucesso";
import DialogErro from "../Agendas/DialogErro";
import DialogDetalhes from "../Caixa/DialogDetalhes";
import DialogHistoricoPaciente from "../Agendas/DialogHistoricoPaciente";
import DialogEditarReserva from "./DialogEditarReserva";

import {
  FaCalendarAlt,
  FaUserMd,
  FaBuilding,
  FaList,
  FaUser,
  FaClock,
  FaUserCircle,
  FaIdCard,
  FaPhone,
  FaEnvelope,
} from "react-icons/fa";

const Reservas = () => {
  const { user } = useAuth();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [agendamentos, setAgendamentos] = useState([]);
  const [visualizacaoResumida, setVisualizacaoResumida] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Estados para os dialogs de confirmação
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");

  // Estados para os diálogos reutilizáveis
  const [visibleDetalhes, setVisibleDetalhes] = useState(false);
  const [reservaDetalhes, setReservaDetalhes] = useState(null);
  const [visibleHistorico, setVisibleHistorico] = useState(false);
  const [pacienteHistorico, setPacienteHistorico] = useState(null);
  const [visibleEditarReserva, setVisibleEditarReserva] = useState(false);
  const [reservaEditandoId, setReservaEditandoId] = useState(null);

  // Estados para Cancelar
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [itemCancela, setItemCancela] = useState(null);

  // Estados principais simplificados
  const [dadosCompletos, setDadosCompletos] = useState({
    empresas: [],
    profissionais: [],
    servicos: [],
    datas_atendimento: [],
    horarios: [],
    agendamentos_existentes: [],
  });

  // Estados para os filtros em cascata (usados pelo DialogEditarReserva)
  const [mostrarCalendario, setMostrarCalendario] = useState(false);
  const [profissionaisFiltrados, setProfissionaisFiltrados] = useState([]);
  const [datasDisponiveis, setDatasDisponiveis] = useState([]);
  const [horariosDisponiveis, setHorariosDisponiveis] = useState([]);
  const [servicosFiltrados, setServicosFiltrados] = useState([]);

  // Estados para Datas
  const [diaSelecionado, setDiaSelecionado] = useState(new Date());
  const [mesCalendario, setMesCalendario] = useState(new Date());

  // Filtro
  const [mostrarFiltroAgenda, setMostrarFiltroAgenda] = useState(false);
  const [filtroNome, setFiltroNome] = useState("");
  const [filtroCpf, setFiltroCpf] = useState("");
  const [filtroTelefone, setFiltroTelefone] = useState("");
  const [filtroEmail, setFiltroEmail] = useState("");
  const [checked, setChecked] = useState(false);

  // Detecção de dispositivo móvel
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    fetchAgendamentos();
    fetchTodosDados();
  }, []);

  // Agendamentos filtrados
  const agendamentosFiltrados = useMemo(() => {
    return agendamentos.filter((agenda) => {
      const dataAgendamento = agenda.data_atendimento?.split("T")[0];
      const dataSelecionada = `${diaSelecionado.getFullYear()}-${String(
        diaSelecionado.getMonth() + 1,
      ).padStart(2, "0")}-${String(diaSelecionado.getDate()).padStart(2, "0")}`;

      const dataMatch = dataAgendamento === dataSelecionada;
      const nomeMatch =
        !filtroNome ||
        agenda.paciente_nome?.toLowerCase().includes(filtroNome.toLowerCase());
      const cpfMatch =
        !filtroCpf ||
        agenda.paciente_cpf?.toLowerCase().includes(filtroCpf.toLowerCase());
      const telefoneMatch =
        !filtroTelefone ||
        agenda.paciente_telefone
          ?.toLowerCase()
          .includes(filtroTelefone.toLowerCase());
      const emailMatch =
        !filtroEmail ||
        agenda.paciente_email
          ?.toLowerCase()
          .includes(filtroEmail.toLowerCase());

      return dataMatch && nomeMatch && cpfMatch && telefoneMatch && emailMatch;
    });
  }, [
    agendamentos,
    diaSelecionado,
    filtroNome,
    filtroCpf,
    filtroTelefone,
    filtroEmail,
  ]);

  const handleSwitchChange = (e) => {
    const newValue = e.value;
    setChecked(newValue);
    fetchAgendamentos(newValue);
  };

  const fetchAgendamentos = async (showAll = false) => {
    setLoading(true);
    setError(null);

    try {
      if (!user?.token) {
        throw new Error("Usuário não autenticado. Faça login novamente.");
      }

      const url =
        "https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/agendamentos";

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Erro ao buscar agendas");
      }

      const result = await response.json();

      if (result.success && Array.isArray(result.data)) {
        let filteredData = result.data;

        if (!showAll) {
          filteredData = result.data.filter(
            (agenda) => agenda.status_reserva === "pendente",
          );
        } else {
          filteredData = result.data.filter(
            (agenda) =>
              agenda.status_reserva === "cancelada" ||
              agenda.status_reserva === "pendente",
          );
        }

        setAgendamentos(filteredData);
        setError(null);
      } else {
        setAgendamentos([]);
        setError("Nenhuma agenda encontrada.");
      }
    } catch (error) {
      setError(error.message);
      setAgendamentos([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTodosDados = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        "https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/agendamento/dados",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user.token}`,
            Accept: "application/json",
          },
        },
      );

      if (!response.ok)
        throw new Error(`HTTP ${response.status} - ${response.statusText}`);

      const result = await response.json();

      if (result.success && result.data) {
        setDadosCompletos({
          empresas: result.data.empresas || [],
          profissionais: result.data.profissionais || [],
          servicos: result.data.servicos || [],
          datas_atendimento: result.data.datas_atendimento || [],
          horarios: result.data.horarios || [],
          agendamentos_existentes: result.data.agendamentos_existentes || [],
        });

        setProfissionaisFiltrados(
          result.data.profissionais.map((prof) => ({
            label: prof.nome,
            value: prof.id,
          })),
        );

        setServicosFiltrados(
          result.data.servicos.map((serv) => ({
            label: `${serv.nome} - ${serv.duracao}min - R$ ${parseFloat(serv.valor).toFixed(2)}`,
            value: serv.id,
            nome: serv.nome,
            duracao: serv.duracao,
            valor: parseFloat(serv.valor).toFixed(2),
            descricao: serv.nome,
          })),
        );
      } else {
        throw new Error(result.message || "Erro na estrutura dos dados");
      }
    } catch (error) {
      console.error("❌ Erro ao carregar dados:", error);
      setError(error.message);
      setDadosCompletos({
        empresas: [],
        profissionais: [],
        servicos: [],
        datas_atendimento: [],
        horarios: [],
        agendamentos_existentes: [],
      });
    } finally {
      setLoading(false);
    }
  };

  const confirmarReserva = async (agendamentoId, pacienteNome) => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/agendamentos/${agendamentoId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
            Accept: "application/json",
          },
          body: JSON.stringify({
            status_reserva: "confirmada",
            status: "confirmado",
            confirmado_por: user.id,
          }),
        },
      );

      const data = await response.json();

      if (data.success) {
        setAgendamentos((prev) =>
          prev.filter((item) => item.id !== agendamentoId),
        );
        showSuccessMessage(
          `Agendamento de ${pacienteNome} confirmado com sucesso!`,
        );
      } else {
        throw new Error(data.message || "Erro ao confirmar reserva");
      }
    } catch (error) {
      console.error("Erro:", error);
      showErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const rejeitarReserva = async (agendamentoId, pacienteNome) => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/agendamentos/${agendamentoId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status_reserva: "cancelada",
            status: "cancelado",
          }),
        },
      );

      const data = await response.json();

      if (data.success) {
        setAgendamentos((prev) =>
          prev.filter((item) => item.id !== agendamentoId),
        );
        showSuccessMessage(
          `Agendamento de ${pacienteNome} cancelado com sucesso!`,
        );
      } else {
        throw new Error(data.message || "Erro ao rejeitar reserva");
      }
    } catch (error) {
      console.error("Erro:", error);
      showErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================================
  // FUNÇÕES DE FILTROS (mantidas iguais)
  // ==========================================================================
  const filtrarProfissionaisPorEmpresa = (empresaId) => {
    /* manter original */
  };
  const filtrarDatasPorProfissional = (profissionalId) => {
    /* manter original */
  };
  const filtrarHorariosPorProfissionalEData = (profissionalId, data) => {
    /* manter original */
  };
  const filtrarServicosPorProfissional = (profissionalId) => {
    /* manter original */
  };


  const formatarData = (data) => {
    return new Date(data + "T00:00:00").toLocaleDateString("pt-BR");
  };

  const formatarHorario = (horario) => {
    return horario;
  };

  const showSuccessMessage = (message) => {
    setDialogMessage(message);
    setShowSuccessDialog(true);
  };

  const showErrorMessage = (message) => {
    setDialogMessage(message);
    setShowErrorDialog(true);
  };

  // Funções do calendário
  const verificarReservasNoDia = (data) => {
    if (!agendamentos || agendamentos.length === 0) return false;

    const dataFormatada = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, "0")}-${String(data.getDate()).padStart(2, "0")}`;

    return agendamentos.some(
      (reserva) => reserva.data_atendimento?.split("T")[0] === dataFormatada,
    );
  };

  const gerarDiasCalendario = () => {
    const ano = mesCalendario.getFullYear();
    const mes = mesCalendario.getMonth();

    const primeiroDia = new Date(ano, mes, 1);
    const ultimoDia = new Date(ano, mes + 1, 0);
    const diaSemanaInicio = primeiroDia.getDay();

    const dias = [];

    for (let i = diaSemanaInicio - 1; i >= 0; i--) {
      const data = new Date(ano, mes, -i);
      dias.push({
        data,
        disponivel: true,
        selecionado: false,
        possuiReserva: verificarReservasNoDia(data),
        outroMes: true,
      });
    }

    for (let dia = 1; dia <= ultimoDia.getDate(); dia++) {
      const data = new Date(ano, mes, dia);

      const dataFormatada = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, "0")}-${String(data.getDate()).padStart(2, "0")}`;
      const diaSelecionadoFormatado = `${diaSelecionado.getFullYear()}-${String(diaSelecionado.getMonth() + 1).padStart(2, "0")}-${String(diaSelecionado.getDate()).padStart(2, "0")}`;

      const selecionado = diaSelecionadoFormatado === dataFormatada;
      const possuiReserva = verificarReservasNoDia(data);

      dias.push({
        data,
        disponivel: true,
        selecionado,
        possuiReserva,
        outroMes: false,
      });
    }

    const totalCells = 42;
    while (dias.length < totalCells) {
      const data = new Date(
        ano,
        mes + 1,
        dias.length - ultimoDia.getDate() - diaSemanaInicio + 1,
      );
      dias.push({
        data,
        disponivel: true,
        selecionado: false,
        possuiReserva: verificarReservasNoDia(data),
        outroMes: true,
      });
    }

    return dias;
  };

  const handleSelecionarDia = (dia) => {
    if (!dia.disponivel) return;
    setDiaSelecionado(new Date(dia.data));
  };

  const handleMudarMes = (direcao) => {
    const novoMes = new Date(mesCalendario);
    if (direcao === "proximo") {
      novoMes.setMonth(novoMes.getMonth() + 1);
    } else {
      novoMes.setMonth(novoMes.getMonth() - 1);
    }
    setMesCalendario(novoMes);
  };

  const buscarFichaPaciente = async (pacienteId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `https://helder.cantarellisolucoes.com.br/wp-json/custom/v1/agendas/${pacienteId}/paciente`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user.token}`,
            Accept: "application/json",
          },
        },
      );

      if (!response.ok)
        throw new Error(`HTTP ${response.status} - ${response.statusText}`);

      const data = await response.json();

      if (data.success && data.data) {
        setPacienteHistorico(data.data);
      } else {
        throw new Error(data.message || "Erro na estrutura dos dados");
      }
    } catch (error) {
      console.error("❌ Erro ao carregar dados:", error);
      setError(error.message);
      setPacienteHistorico([]);
    } finally {
      setLoading(false);
    }
  };

  const irParaDiaAnterior = () => {
    const novoDia = new Date(diaSelecionado);
    novoDia.setDate(novoDia.getDate() - 1);
    setDiaSelecionado(novoDia);
  };

  const irParaProximoDia = () => {
    const novoDia = new Date(diaSelecionado);
    novoDia.setDate(novoDia.getDate() + 1);
    setDiaSelecionado(novoDia);
  };

  const openCancelaConfirm = (id) => {
    setItemCancela(id);
    setShowConfirmDialog(true);
  };

  // Funções para abrir os diálogos
  const abrirDetalhes = (item) => {
    setReservaDetalhes(item);
    setVisibleDetalhes(true);
  };

  const abrirHistorico = async (item) => {
    setReservaEditandoId(item.id);
    await buscarFichaPaciente(item.paciente_id);
    setVisibleHistorico(true);
  };

  const abrirEdicao = (itemId) => {
    setReservaEditandoId(itemId);
    setVisibleEditarReserva(true);
  };

  return (
    <div
      style={{
        fontFamily: "'Rajdhani', sans-serif",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
      }}
    >
      {/* ===== DIÁLOGOS REUTILIZÁVEIS ===== */}
      <DialogSucesso
        visible={showSuccessDialog}
        onHide={() => {
          setShowSuccessDialog(false);
        }}
        message={dialogMessage}
        onConfirm={() => {
          setShowSuccessDialog(false);
        }}
      />

      <DialogErro
        visible={showErrorDialog}
        onHide={() => setShowErrorDialog(false)}
        message={dialogMessage}
      />

      {/* Dialog de Confirmação de Cancelamento */}
      <Dialog
        visible={showConfirmDialog}
        style={{ width: "350px", fontFamily: "'Rajdhani', sans-serif" }}
        onHide={() => setShowConfirmDialog(false)}
        draggable={false}
        closable={false}
        header={null}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "20px",
            padding: "10px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "25px",
              height: "25px",
              borderRadius: "50%",
              color: "#ef4444",
              fontSize: "2.2rem",
            }}
          >
            <i className="pi pi-question-circle" style={{ fontSize: "2.5rem" }}></i>
          </div>
          <p
            style={{
              textAlign: "center",
              fontSize: "1rem",
              margin: 0,
              color: "#374151",
            }}
          >
            Tem certeza que deseja cancelar esta reserva?
          </p>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "12px",
              width: "100%",
            }}
          >
            <Button
              label="NÃO"
              icon="pi pi-times"
              onClick={() => setShowConfirmDialog(false)}
              className="p-button-outlined p-button-sm"
              style={{
                outline: "none",
                boxShadow: "none",
                padding: "6px 20px",
                minWidth: "100px",
                borderColor: "#dee2e6",
                color: "#495057",
                fontWeight: "500",
                fontSize: "0.875rem",
              }}
            />
            <Button
              label="SIM"
              icon="pi pi-check"
              onClick={() => {
                if (itemCancela) {
                  const item = agendamentosFiltrados.find(
                    (ag) => ag.id === itemCancela,
                  );
                  if (item) {
                    rejeitarReserva(item.id, item.paciente_nome);
                  }
                }
                setShowConfirmDialog(false);
              }}
              className="p-button-success p-button-sm"
              style={{
                outline: "none",
                boxShadow: "none",
                padding: "6px 20px",
                minWidth: "100px",
                fontWeight: "500",
                fontSize: "0.875rem",
              }}
            />
          </div>

          <div
            style={{
              textAlign: "center",
              marginTop: "5px",
              padding: "10px",
              backgroundColor: "#fff5f5",
              borderRadius: "6px",
              border: "1px solid #f8d7da",
            }}
          >
            <div
              style={{
                fontSize: "0.8rem",
                color: "#721c24",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "5px",
              }}
            >
              <span>O paciente será notificado sobre o cancelamento.</span>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Dialog de Detalhes (usando DialogDetalhes do Caixa) */}
      <DialogDetalhes
        visible={visibleDetalhes}
        onHide={() => {
          setVisibleDetalhes(false);
          setReservaDetalhes(null);
        }}
        dados={reservaDetalhes}
        formatarData={formatarData}
        formatarHorario={formatarHorario}
      />

      {/* Dialog do Histórico (usando DialogHistoricoPaciente do Agendas) */}
      <DialogHistoricoPaciente
        visible={visibleHistorico}
        onHide={() => {
          setVisibleHistorico(false);
          setPacienteHistorico(null);
          setReservaEditandoId(null);
        }}
        pacienteEscolhido={pacienteHistorico}
        reservaEditando={reservaEditandoId ? { id: reservaEditandoId, paciente_nome: "Paciente" } : null}
      />

      {/* Dialog de Editar Reserva */}
      <DialogEditarReserva
        visible={visibleEditarReserva}
        onHide={() => {
          setVisibleEditarReserva(false);
          setReservaEditandoId(null);
        }}
        reservaEditandoId={reservaEditandoId}
        dadosCompletos={dadosCompletos}
        profissionaisFiltrados={profissionaisFiltrados}
        datasDisponiveis={datasDisponiveis}
        horariosDisponiveis={horariosDisponiveis}
        servicosFiltrados={servicosFiltrados}
        filtrarProfissionaisPorEmpresa={filtrarProfissionaisPorEmpresa}
        filtrarDatasPorProfissional={filtrarDatasPorProfissional}
        filtrarHorariosPorProfissionalEData={filtrarHorariosPorProfissionalEData}
        fetchAgendamentos={fetchAgendamentos}
        fetchTodosDados={fetchTodosDados}
        showSuccessMessage={showSuccessMessage}
        showErrorMessage={showErrorMessage}
        checked={checked}
      />

      {/* ===== BARRA SUPERIOR ===== */}
      <div
        style={{
          backgroundColor: "#B83732",
          height: "40px",
          minHeight: "40px",
          width: "100%",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 20px",
          fontWeight: "bold",
          position: "fixed",
          zIndex: 1,
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <div></div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Button
            icon="pi pi-refresh"
            className="icon-button-titulo"
            onClick={() => {
              fetchAgendamentos();
              setChecked(false);
              setDiaSelecionado(new Date());
            }}
            style={{
              border: "none",
              outline: "none",
              boxShadow: "none",
              color: "#ffffff",
              display: "inline-flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            outlined
          />
          <Button
            icon={visualizacaoResumida ? "pi pi-eye" : "pi pi-eye-slash"}
            className="icon-button-titulo"
            onClick={() => setVisualizacaoResumida(!visualizacaoResumida)}
            style={{
              border: "none",
              outline: "none",
              boxShadow: "none",
              color: "#ffffff",
              display: "inline-flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            outlined
          />
          <Button
            icon={mostrarFiltroAgenda ? "pi pi-filter-slash" : "pi pi-filter"}
            className="icon-button-titulo"
            onClick={() => setMostrarFiltroAgenda(!mostrarFiltroAgenda)}
            style={{
              border: "none",
              outline: "none",
              boxShadow: "none",
              color: "#ffffff",
              display: "inline-flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            outlined
          />
          <Button
            icon={
              mostrarCalendario ? "pi pi-calendar-minus" : "pi pi-calendar-plus"
            }
            className="icon-button-titulo"
            onClick={() => setMostrarCalendario(!mostrarCalendario)}
            style={{
              border: "none",
              outline: "none",
              boxShadow: "none",
              color: "#ffffff",
              display: "inline-flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            outlined
          />
        </div>
        <div></div>
      </div>

      {/* ===== CONTEÚDO PRINCIPAL ===== */}
      <div style={{ flex: 1, padding: "50px 10px" }}>
        <div style={{ borderRadius: "8px", padding: "0 15px" }}>
          {/* Cabeçalho */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #E2E8F0",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <FaCalendarAlt style={{ color: "#B83732", fontSize: "1.2rem" }} />
              <h3 style={{ margin: 0, color: "#424242" }}>RESERVAS</h3>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "25px",
                  height: "25px",
                  background:
                    "linear-gradient(135deg, #B83732 0%, #D65A55 100%)",
                  borderRadius: "50px",
                  padding: "0 8px",
                  margin: "0 8px",
                  boxShadow: "0 2px 4px rgba(184, 55, 50, 0.2)",
                }}
              >
                <span
                  style={{
                    color: "white",
                    fontSize: "0.85rem",
                    fontWeight: "600",
                    lineHeight: 1,
                  }}
                >
                  {agendamentosFiltrados.length}
                </span>
              </div>
            </div>
            <div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
              <Button
                icon="pi pi-chevron-left"
                className="p-button-text p-button-sm"
                onClick={irParaDiaAnterior}
                tooltip="Dia anterior"
                style={{
                  border: "none",
                  outline: "none",
                  boxShadow: "none",
                  color: "#424242",
                }}
              />
              <h3 style={{ margin: 0, color: "#424242" }}>
                {diaSelecionado.toLocaleDateString("pt-BR")}
              </h3>
              <Button
                icon="pi pi-chevron-right"
                className="p-button-text p-button-sm"
                onClick={irParaProximoDia}
                tooltip="Próximo Dia"
                style={{
                  border: "none",
                  outline: "none",
                  boxShadow: "none",
                  color: "#424242",
                }}
              />
            </div>
          </div>

          {/* Switch Mostrar Todos */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "end",
              marginBottom: "12px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <InputSwitch
                checked={checked}
                onChange={handleSwitchChange}
                style={{ transform: "scale(0.8)" }}
              />
              <span style={{ fontSize: "0.85rem", color: "#4A5568" }}>
                Mostrar todos
              </span>
            </div>
          </div>

          {/* CALENDÁRIO */}
          {mostrarCalendario && (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div
                style={{
                  borderRadius: "8px",
                  padding: "10px 8px",
                  border: "1px solid #dee2e6",
                  backgroundColor: "#fff",
                  width: "95%",
                  maxWidth: "920px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginBottom: "5px",
                  }}
                >
                  <Button
                    icon="pi pi-chevron-left"
                    className="p-button-text p-button-sm"
                    onClick={() => handleMudarMes("anterior")}
                    style={{
                      border: "none",
                      outline: "none",
                      boxShadow: "none",
                      color: "#424242",
                      width: "30px",
                      height: "30px",
                    }}
                  />
                  <span
                    style={{
                      fontSize: "1rem",
                      fontWeight: "bold",
                      color: "#424242",
                      margin: "0 10px",
                    }}
                  >
                    {mesCalendario
                      .toLocaleDateString("pt-BR", {
                        month: "long",
                        year: "numeric",
                      })
                      .toUpperCase()}
                  </span>
                  <Button
                    icon="pi pi-chevron-right"
                    className="p-button-text p-button-sm"
                    onClick={() => handleMudarMes("proximo")}
                    style={{
                      border: "none",
                      outline: "none",
                      boxShadow: "none",
                      color: "#424242",
                      width: "30px",
                      height: "30px",
                    }}
                  />
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, 1fr)",
                    gap: "2px",
                    marginBottom: "10px",
                  }}
                >
                  {["DOM", "SEG", "TER", "QUA", "QUI", "SEX", "SÁB"].map(
                    (dia) => (
                      <div
                        key={dia}
                        style={{
                          textAlign: "center",
                          fontWeight: "bold",
                          padding: "5px 2px",
                          backgroundColor: "#e9ecef",
                          borderRadius: "3px",
                          fontSize: "0.8rem",
                          color: "#495057",
                        }}
                      >
                        {dia}
                      </div>
                    ),
                  )}

                  {gerarDiasCalendario().map((dia, index) => {
                    const clicavel =
                      dia.disponivel !== false ||
                      (dia.possuiReserva && dia.disponivel !== false);

                    let backgroundColor = "#ffffff";
                    if (dia.selecionado) backgroundColor = "#fff0f0";
                    else if (dia.outroMes) backgroundColor = "#f8f9fa";
                    else if (dia.possuiReserva) backgroundColor = "#e8f5e9";

                    let color = "#424242";
                    if (dia.outroMes) color = "#adb5bd";
                    else if (dia.selecionado) color = "#b83732";
                    else if (dia.possuiReserva) color = "#28a745";

                    let borderColor = "#dee2e6";
                    if (dia.selecionado) borderColor = "#a8322d";
                    else if (dia.possuiReserva) borderColor = "#28a745";

                    return (
                      <div
                        key={index}
                        onClick={() => clicavel && handleSelecionarDia(dia)}
                        style={{
                          textAlign: "center",
                          padding: "8px 1px",
                          backgroundColor,
                          borderRadius: "4px",
                          cursor: clicavel ? "pointer" : "not-allowed",
                          border: `1px solid ${borderColor}`,
                          fontWeight: dia.selecionado ? "bold" : "normal",
                          color,
                          position: "relative",
                          height: "45px",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          fontSize: "0.85rem",
                          opacity: dia.outroMes ? 0.6 : 1,
                          boxSizing: "border-box",
                        }}
                      >
                        <div
                          style={{
                            fontWeight:
                              dia.data.getDate() === new Date().getDate() &&
                              !dia.outroMes
                                ? "bold"
                                : "normal",
                            fontSize: "0.85rem",
                          }}
                        >
                          {dia.data.getDate()}
                        </div>

                        {dia.possuiReserva && !dia.outroMes && (
                          <div
                            style={{
                              position: "absolute",
                              top: "3px",
                              right: "3px",
                              width: "8px",
                              height: "8px",
                              backgroundColor: "#28a745",
                              borderRadius: "50%",
                            }}
                          />
                        )}

                        {dia.data.getDate() === new Date().getDate() &&
                          dia.data.getMonth() === new Date().getMonth() &&
                          dia.data.getFullYear() === new Date().getFullYear() &&
                          !dia.outroMes && (
                            <div
                              style={{
                                position: "absolute",
                                bottom: "2px",
                                width: "4px",
                                height: "4px",
                                backgroundColor: "#007bff",
                                borderRadius: "50%",
                              }}
                            />
                          )}
                      </div>
                    );
                  })}
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "15px",
                    flexWrap: "wrap",
                    fontSize: "0.75rem",
                    paddingTop: "10px",
                    borderTop: "1px solid #e0e0e0",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <div
                      style={{
                        width: "12px",
                        height: "12px",
                        backgroundColor: "#b83732",
                        borderRadius: "2px",
                      }}
                    />
                    <span style={{ color: "#6c757d" }}>Selecionado</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <div
                      style={{
                        width: "12px",
                        height: "12px",
                        backgroundColor: "#28a745",
                        borderRadius: "2px",
                      }}
                    />
                    <span style={{ color: "#6c757d" }}>Com agenda</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <div
                      style={{
                        width: "12px",
                        height: "12px",
                        backgroundColor: "#ffffff",
                        border: "1px solid #007bff",
                        borderRadius: "2px",
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          bottom: "1px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: "4px",
                          height: "4px",
                          backgroundColor: "#007bff",
                          borderRadius: "50%",
                        }}
                      />
                    </div>
                    <span style={{ color: "#6c757d" }}>Hoje</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* FILTROS */}
          {mostrarFiltroAgenda && (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div
                style={{
                  borderRadius: "8px",
                  padding: "10px 8px",
                  border: "1px solid #dee2e6",
                  backgroundColor: "#fff",
                  width: "95%",
                  maxWidth: "920px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                    gap: "10px",
                  }}
                >
                  <div style={{ padding: "10px" }}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          paddingBottom: "5px",
                          borderBottom: "2px solid #3498DB",
                        }}
                      >
                        <FaUser
                          style={{ color: "#3498DB", fontSize: "0.9rem" }}
                        />
                        <span
                          style={{
                            fontWeight: "600",
                            color: "#2c3e50",
                            fontSize: "0.95rem",
                          }}
                        >
                          Nome
                        </span>
                      </div>
                      <InputText
                        className="p-inputtext-sm"
                        placeholder="Nome do paciente"
                        value={filtroNome}
                        onChange={(e) => setFiltroNome(e.target.value)}
                        style={{
                          outline: "none",
                          boxShadow: "none",
                          width: "100%",
                          height: "35px",
                          fontFamily: "'Rajdhani', sans-serif",
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ padding: "10px" }}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          paddingBottom: "5px",
                          borderBottom: "2px solid #E67E22",
                        }}
                      >
                        <FaIdCard
                          style={{ color: "#E67E22", fontSize: "0.9rem" }}
                        />
                        <span
                          style={{
                            fontWeight: "600",
                            color: "#2c3e50",
                            fontSize: "0.95rem",
                          }}
                        >
                          CPF
                        </span>
                      </div>
                      <InputText
                        className="p-inputtext-sm"
                        placeholder="CPF do paciente"
                        value={filtroCpf}
                        onChange={(e) => setFiltroCpf(e.target.value)}
                        style={{
                          outline: "none",
                          boxShadow: "none",
                          width: "100%",
                          height: "35px",
                          fontFamily: "'Rajdhani', sans-serif",
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ padding: "10px" }}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          paddingBottom: "5px",
                          borderBottom: "2px solid #2980b9",
                        }}
                      >
                        <FaPhone
                          style={{ color: "#2980b9", fontSize: "0.9rem" }}
                        />
                        <span
                          style={{
                            fontWeight: "600",
                            color: "#2c3e50",
                            fontSize: "0.95rem",
                          }}
                        >
                          Telefone
                        </span>
                      </div>
                      <InputText
                        className="p-inputtext-sm"
                        placeholder="Telefone do paciente"
                        value={filtroTelefone}
                        onChange={(e) => setFiltroTelefone(e.target.value)}
                        style={{
                          outline: "none",
                          boxShadow: "none",
                          width: "100%",
                          height: "35px",
                          fontFamily: "'Rajdhani', sans-serif",
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ padding: "10px" }}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          paddingBottom: "5px",
                          borderBottom: "2px solid #d35400",
                        }}
                      >
                        <FaEnvelope
                          style={{ color: "#d35400", fontSize: "0.9rem" }}
                        />
                        <span
                          style={{
                            fontWeight: "600",
                            color: "#2c3e50",
                            fontSize: "0.95rem",
                          }}
                        >
                          Email
                        </span>
                      </div>
                      <InputText
                        className="p-inputtext-sm"
                        placeholder="Email do paciente"
                        value={filtroEmail}
                        onChange={(e) => setFiltroEmail(e.target.value)}
                        style={{
                          outline: "none",
                          boxShadow: "none",
                          width: "100%",
                          height: "35px",
                          fontFamily: "'Rajdhani', sans-serif",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* LISTA DE RESERVAS */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              padding: "10px 8px",
            }}
          >
            {agendamentosFiltrados.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 20px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "8px",
                  border: "2px dashed #e8e8e8",
                }}
              >
                <FaCalendarAlt
                  style={{
                    fontSize: "3rem",
                    color: "#B83732",
                    marginBottom: "15px",
                    opacity: 0.5,
                  }}
                />
                <h3
                  style={{
                    color: "#2c3e50",
                    margin: "0 0 10px 0",
                    fontSize: "1.2rem",
                    fontWeight: "600",
                  }}
                >
                  Nenhuma reserva encontrada
                </h3>
              </div>
            ) : !visualizacaoResumida ? (
              /* VISUALIZAÇÃO EM LINHA */
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                {agendamentosFiltrados.map((item, idx) => (
                  <div key={idx}>
                    <div
                      style={{
                        backgroundColor: "#ffffff",
                        border: "1px solid #e8e8e8",
                        borderRadius: "10px",
                        padding: "12px 14px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "12px",
                        flexWrap: "wrap",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          minWidth: isMobile ? "180px" : "260px",
                          flex: "1 1 260px",
                        }}
                      >
                        <FaUserCircle
                          style={{
                            fontSize: isMobile ? "1.8rem" : "2.5rem",
                            color: "#D65A55",
                            flexShrink: 0,
                          }}
                        />
                        <div style={{ minWidth: 0 }}>
                          <h3
                            style={{
                              fontSize: "1rem",
                              fontWeight: "600",
                              color: "#2c3e50",
                              margin: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.paciente_nome}
                          </h3>
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "5px",
                              alignItems: "center",
                              color: "#64748b",
                              fontSize: "0.9rem",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                marginRight: "6px",
                              }}
                            >
                              <i
                                className="pi pi-phone"
                                style={{ fontSize: "0.7rem" }}
                              />
                              <span>{item.paciente_telefone}</span>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                color: "#64748b",
                                fontSize: "0.85rem",
                              }}
                            >
                              <i
                                className="pi pi-tag"
                                style={{ fontSize: "0.7rem" }}
                              />
                              <span>{item.id}</span>
                            </div>
                            {!isMobile && (
                              <>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    marginRight: "6px",
                                  }}
                                >
                                  <FaCalendarAlt
                                    style={{ fontSize: "0.7rem", color: "#2ecc71" }}
                                  />
                                  <span>{formatarData(item.data_atendimento)}</span>
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    marginRight: "6px",
                                  }}
                                >
                                  <FaClock
                                    style={{ fontSize: "0.7rem", color: "#f39c12" }}
                                  />
                                  <span>{formatarHorario(item.horario)}</span>
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <FaBuilding
                                    style={{ fontSize: "0.7rem", color: "#3498db" }}
                                  />
                                  <span>{item.empresa_nome}</span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: "8px",
                          flex: "1 1 320px",
                          marginLeft: "auto",
                          flexWrap: "wrap",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: "5px",
                            flexWrap: "wrap",
                          }}
                        >
                          {item.status === "cancelado" ? (
                            <span
                              style={{
                                backgroundColor: "#f8d7da",
                                color: "#721c24",
                                padding: "4px 10px",
                                borderRadius: "20px",
                                fontSize: "0.75rem",
                                fontWeight: "600",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <i
                                className="pi pi-times"
                                style={{ fontSize: "0.7rem" }}
                              />
                              CANCELADA
                            </span>
                          ) : (
                            <span
                              style={{
                                backgroundColor: "#fff3cd",
                                color: "#856404",
                                padding: "4px 10px",
                                borderRadius: "20px",
                                fontSize: "0.75rem",
                                fontWeight: "600",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <i
                                className="pi pi-clock"
                                style={{ fontSize: "0.7rem" }}
                              />
                              RESERVADO
                            </span>
                          )}
                        </div>

                        {item.status !== "realizado" && item.status !== "cancelado" && (
                          <div style={{ display: "flex", gap: "5px" }}>
                            <Button
                              icon="pi pi-check"
                              onClick={() => {
                                if (
                                  user.tipo === "administrator" ||
                                  user.modulos?.Reservas?.editar
                                ) {
                                  confirmarReserva(
                                    item.id,
                                    item.paciente_nome,
                                  );
                                }
                              }}
                              disabled={
                                !(
                                  user.tipo === "administrator" ||
                                  user.modulos?.Reservas?.editar
                                )
                              }
                              className="p-button-success p-button-sm"
                              style={{
                                outline: "none",
                                boxShadow: "none",
                                width: "25px",
                                height: "25px",
                                borderColor: "#dee2e6",
                              }}
                              tooltip="Aceitar"
                              tooltipOptions={{ position: "top" }}
                            />
                            <Button
                              icon="pi pi-times"
                              onClick={() => {
                                if (
                                  user.tipo === "administrator" ||
                                  user.modulos?.Reservas?.editar
                                ) {
                                  openCancelaConfirm(item.id);
                                }
                              }}
                              disabled={
                                !(
                                  user.tipo === "administrator" ||
                                  user.modulos?.Reservas?.editar
                                )
                              }
                              className="p-button-danger p-button-sm"
                              style={{
                                outline: "none",
                                boxShadow: "none",
                                width: "25px",
                                height: "25px",
                                borderRadius: "6px",
                              }}
                              tooltip="Cancelar"
                              tooltipOptions={{ position: "top" }}
                            />
                          </div>
                        )}

                        <div style={{ display: "flex", gap: "5px" }}>
                          <Button
                            icon="pi pi-info-circle"
                            onClick={() => abrirDetalhes(item)}
                            className="p-button-outlined p-button-sm"
                            style={{
                              outline: "none",
                              boxShadow: "none",
                              width: "25px",
                              height: "25px",
                              borderColor: "#dee2e6",
                              color: "#495057",
                            }}
                            tooltip="Detalhes"
                            tooltipOptions={{ position: "top" }}
                          />
                          <Button
                            icon="pi pi-history"
                            onClick={() => abrirHistorico(item)}
                            className="p-button-outlined p-button-sm"
                            style={{
                              outline: "none",
                              boxShadow: "none",
                              width: "25px",
                              height: "25px",
                              borderColor: "#dee2e6",
                              color: "#495057",
                            }}
                            tooltip="Histórico"
                            tooltipOptions={{ position: "top" }}
                          />
                          <Button
                            icon="pi pi-file-edit"
                            onClick={() => abrirEdicao(item.id)}
                            disabled={
                              !(
                                user.tipo === "administrator" ||
                                user.modulos?.Reservas?.editar
                              ) || item.status === "cancelado"
                            }
                            className="p-button-outlined p-button-sm"
                            style={{
                              outline: "none",
                              boxShadow: "none",
                              width: "25px",
                              height: "25px",
                              borderColor: "#dee2e6",
                              color: "#495057",
                            }}
                            tooltip="Editar"
                            tooltipOptions={{ position: "top" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* VISUALIZAÇÃO EM GRADE */
              <div
                className="grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                  gap: "15px",
                  alignItems: "stretch",
                }}
              >
                {agendamentosFiltrados.map((item, idx) => (
                  <div key={idx}>
                    <div
                      style={{
                        backgroundColor: "#ffffff",
                        border: "1px solid #e8e8e8",
                        borderRadius: "10px",
                        padding: "12px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          marginBottom: "12px",
                        }}
                      >
                        <div>
                          {item.status === "cancelado" ? (
                            <span
                              style={{
                                backgroundColor: "#f8d7da",
                                color: "#721c24",
                                padding: "4px 10px",
                                borderRadius: "20px",
                                fontSize: "0.85rem",
                                fontWeight: "600",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <i
                                className="pi pi-times"
                                style={{ fontSize: "0.7rem" }}
                              ></i>
                              CANCELADA
                            </span>
                          ) : (
                            <span
                              style={{
                                backgroundColor: "#fff3cd",
                                color: "#856404",
                                padding: "4px 10px",
                                borderRadius: "20px",
                                fontSize: "0.85rem",
                                fontWeight: "600",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                            >
                              <i
                                className="pi pi-clock"
                                style={{ fontSize: "0.7rem" }}
                              ></i>
                              RESERVADO
                            </span>
                          )}
                        </div>

                        {item.status !== "realizado" && item.status !== "cancelado" && (
                          <div style={{ display: "flex", gap: "5px" }}>
                            <Button
                              icon="pi pi-check"
                              onClick={() => {
                                if (
                                  user.tipo === "administrator" ||
                                  user.modulos?.Reservas?.editar
                                ) {
                                  confirmarReserva(
                                    item.id,
                                    item.paciente_nome,
                                  );
                                }
                              }}
                              disabled={
                                !(
                                  user.tipo === "administrator" ||
                                  user.modulos?.Reservas?.editar
                                )
                              }
                              className="p-button-success p-button-sm"
                              style={{
                                outline: "none",
                                boxShadow: "none",
                                width: "25px",
                                height: "25px",
                                borderColor: "#dee2e6",
                              }}
                              tooltip="Aceitar"
                              tooltipOptions={{ position: "top" }}
                            />
                            <Button
                              icon="pi pi-times"
                              onClick={() => {
                                if (
                                  user.tipo === "administrator" ||
                                  user.modulos?.Reservas?.editar
                                ) {
                                  openCancelaConfirm(item.id);
                                }
                              }}
                              disabled={
                                !(
                                  user.tipo === "administrator" ||
                                  user.modulos?.Reservas?.editar
                                )
                              }
                              className="p-button-danger p-button-sm"
                              style={{
                                outline: "none",
                                boxShadow: "none",
                                width: "25px",
                                height: "25px",
                                borderRadius: "6px",
                              }}
                              tooltip="Cancelar"
                              tooltipOptions={{ position: "top" }}
                            />
                          </div>
                        )}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          marginBottom: "5px",
                          paddingBottom: "5px",
                          borderBottom: "1px solid #e8e8e8",
                        }}
                      >
                        <FaUserCircle
                          style={{
                            fontSize: "2.5rem",
                            color: "#D65A55",
                            flexShrink: 0,
                          }}
                        />
                        <div style={{ flex: 1 }}>
                          <h3
                            style={{
                              fontSize: "1rem",
                              fontWeight: "600",
                              color: "#2c3e50",
                              margin: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.paciente_nome}
                          </h3>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              color: "#64748b",
                              fontSize: "0.85rem",
                            }}
                          >
                            <i
                              className="pi pi-phone"
                              style={{ fontSize: "0.7rem" }}
                            />
                            <span>{item.paciente_telefone}</span>
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            color: "#64748b",
                            fontSize: "0.85rem",
                          }}
                        >
                          <i
                            className="pi pi-tag"
                            style={{ fontSize: "0.7rem" }}
                          />
                          <span>{item.id}</span>
                        </div>
                      </div>

                      {visualizacaoResumida && (
                        <>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: "6px",
                              marginBottom: "5px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                backgroundColor: "#f8f9fa",
                                border: "1px solid #e8e8e8",
                                padding: "6px",
                                borderRadius: "6px",
                                flexDirection: "column",
                                gap: "5px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "5px",
                                }}
                              >
                                <FaCalendarAlt
                                  style={{
                                    color: "#2ecc71",
                                    fontSize: "0.75rem",
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: "0.8rem",
                                    color: "#2c3e50",
                                    fontWeight: "500",
                                  }}
                                >
                                  {formatarData(item.data_atendimento)}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "5px",
                                }}
                              >
                                <FaClock
                                  style={{
                                    color: "#f39c12",
                                    fontSize: "0.75rem",
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: "0.8rem",
                                    color: "#2c3e50",
                                    fontWeight: "500",
                                  }}
                                >
                                  {formatarHorario(item.horario)}
                                </span>
                              </div>
                            </div>

                            <div
                              style={{
                                display: "flex",
                                backgroundColor: "#f8f9fa",
                                border: "1px solid #e8e8e8",
                                padding: "6px",
                                borderRadius: "6px",
                                flexDirection: "column",
                                gap: "5px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "5px",
                                }}
                              >
                                <FaBuilding
                                  style={{
                                    color: "#3498db",
                                    fontSize: "0.75rem",
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: "0.8rem",
                                    color: "#2c3e50",
                                    fontWeight: "500",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {item.empresa_nome}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "5px",
                                }}
                              >
                                <FaUserMd
                                  style={{
                                    color: "#9b59b6",
                                    fontSize: "0.75rem",
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: "0.8rem",
                                    color: "#2c3e50",
                                    fontWeight: "500",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {item.profissional_nome}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div
                            style={{
                              backgroundColor: "#f8f9fa",
                              border: "1px solid #e8e8e8",
                              padding: "6px",
                              borderRadius: "6px",
                              marginBottom: "6px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                              }}
                            >
                              <FaList
                                style={{
                                  color: "#e74c3c",
                                  fontSize: "0.75rem",
                                }}
                              />
                              <span
                                style={{
                                  fontSize: "0.85rem",
                                  color: "#2c3e50",
                                  fontWeight: "500",
                                  display: "block",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {item.servico_nome}
                              </span>
                            </div>
                          </div>
                        </>
                      )}

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "end",
                          paddingTop: "5px",
                          gap: "5px",
                          marginTop: "auto",
                          borderTop: visualizacaoResumida
                            ? "1px solid #e8e8e8"
                            : "",
                        }}
                      >
                        <div style={{ display: "flex", gap: "5px" }}>
                          <Button
                            icon="pi pi-info-circle"
                            onClick={() => abrirDetalhes(item)}
                            className="p-button-outlined p-button-sm"
                            style={{
                              outline: "none",
                              boxShadow: "none",
                              width: "25px",
                              height: "25px",
                              borderColor: "#dee2e6",
                              color: "#495057",
                            }}
                            tooltip="Detalhes"
                            tooltipOptions={{ position: "top" }}
                          />
                          <Button
                            icon="pi pi-history"
                            onClick={() => abrirHistorico(item)}
                            className="p-button-outlined p-button-sm"
                            style={{
                              outline: "none",
                              boxShadow: "none",
                              width: "25px",
                              height: "25px",
                              borderColor: "#dee2e6",
                              color: "#495057",
                            }}
                            tooltip="Histórico"
                            tooltipOptions={{ position: "top" }}
                          />
                          <Button
                            icon="pi pi-file-edit"
                            onClick={() => abrirEdicao(item.id)}
                            disabled={
                              !(
                                user.tipo === "administrator" ||
                                user.modulos?.Reservas?.editar
                              ) || item.status === "cancelado"
                            }
                            className="p-button-outlined p-button-sm"
                            style={{
                              outline: "none",
                              boxShadow: "none",
                              width: "25px",
                              height: "25px",
                              borderColor: "#dee2e6",
                              color: "#495057",
                            }}
                            tooltip="Editar"
                            tooltipOptions={{ position: "top" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {loading && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "200px",
            }}
          >
            <ProgressSpinner
              style={{ width: "50px", height: "50px" }}
              strokeWidth="8"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Reservas;